/**
 * Main Application Component
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Drawer,
  SegmentedControl,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDrop } from "react-dnd";
import {
  IconAdjustments,
  IconBoxMultiple,
  IconRotate360,
} from "@tabler/icons-react";
import { LeftPanel, RightPanel } from "./panels";
import { NoticeToaster } from "./components/NoticeToaster";
import { MobileJointSheet } from "./panels/MobileJointSheet";
import { Scene, getSceneUtils } from "./scene";
import {
  useInstances,
  usePendingPlacementModule,
  useSelectedModuleInstanceId,
  useViewerStore,
} from "./store";
import { useWebSocket } from "./WebSocket";
import { Header } from "./components/Header";
import {
  initUndoSystem,
  recordDelete,
  recordDisconnect,
  undo,
  redo,
  useKeyboardShortcuts,
  useIsMobile,
} from "./hooks";
import { MODULE_DRAG_TYPE } from "./components/ModuleCard";
import type { ModuleData } from "./types";

interface DropItem {
  module: ModuleData;
}

function ViewportDropZone() {
  const { sendAddModule } = useWebSocket();

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: MODULE_DRAG_TYPE,
    drop: (item: DropItem, monitor) => {
      const clientOffset = monitor.getClientOffset();
      let position: [number, number, number] = [0, 0, 0];

      if (clientOffset) {
        const sceneUtils = getSceneUtils();
        if (sceneUtils) {
          const groundPos = sceneUtils.getGroundPosition(clientOffset.x, clientOffset.y);
          if (groundPos) {
            position = groundPos;
          }
        }
      }

      console.log("Dropped module:", item.module.name, "at position:", position);
      sendAddModule(item.module.id, position);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
      canDrop: !!monitor.canDrop(),
    }),
  }), [sendAddModule]);

  return (
    <Box
      ref={drop}
      style={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        outline: isOver && canDrop ? "3px dashed #228be6" : "none",
        outlineOffset: "-3px",
        backgroundColor: isOver && canDrop ? "rgba(34, 139, 230, 0.05)" : "transparent",
      }}
    >
      <Scene />
      {/* Drop indicator overlay */}
      {canDrop && (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isOver && (
            <Box
              style={{
                backgroundColor: "rgba(34, 139, 230, 0.9)",
                color: "white",
                padding: "12px 24px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              Drop to add module
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * Touch replacement for the scene's right-click menu: the same actions, on a
 * bar that appears while something is selected.
 */
function MobileSelectionBar() {
  const instances = useInstances();
  const selectedInstanceId = useSelectedModuleInstanceId();
  const { sendAddModule, sendDeleteInstance, sendDisconnectInstance } = useWebSocket();

  const selected = instances.find((instance) => instance.instance_id === selectedInstanceId);
  if (!selected) {
    return null;
  }

  const handleDuplicate = () => {
    const [x, y, z] = selected.position;
    sendAddModule(selected.module_name, [x + 0.1, y + 0.1, z]);
  };

  const handleDisconnect = () => {
    if (selected.parent_instance_id && selected.parent_link_name && selected.child_link_name) {
      recordDisconnect(
        selected.instance_id,
        selected.child_link_name,
        selected.parent_instance_id,
        selected.parent_link_name
      );
    }
    sendDisconnectInstance(selected.instance_id);
  };

  const handleDelete = () => {
    recordDelete(selected, instances);
    sendDeleteInstance(selected.instance_id);
  };

  return (
    <Box
      style={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 12px",
        borderTop: "1px solid var(--mantine-color-gray-3)",
        backgroundColor: "var(--mantine-color-body)",
      }}
    >
      <Text size="xs" fw={600} lineClamp={1} style={{ flex: 1, wordBreak: "break-all" }}>
        {selected.module_name}
      </Text>
      <Button size="sm" variant="light" onClick={handleDuplicate}>
        Duplicate
      </Button>
      {selected.parent_instance_id != null && (
        <Button size="sm" variant="light" onClick={handleDisconnect}>
          Disconnect
        </Button>
      )}
      <Button size="sm" variant="light" color="red" onClick={handleDelete}>
        Delete
      </Button>
    </Box>
  );
}

type SheetTab = "modules" | "inspector" | "joints";

const SHEET_TABS: { value: SheetTab; label: string; icon: typeof IconBoxMultiple }[] = [
  { value: "modules", label: "Modules", icon: IconBoxMultiple },
  { value: "inspector", label: "Inspector", icon: IconAdjustments },
  { value: "joints", label: "Joints", icon: IconRotate360 },
];

// Posing wants to see the robot, so the joint sheet takes far less of the
// screen than the panels do.
const SHEET_HEIGHTS: Record<SheetTab, string> = {
  modules: "70%",
  inspector: "70%",
  joints: "35%",
};

/**
 * Phone layout for the two side panels: a tab bar pinned under the viewport
 * that raises either panel in a bottom sheet.
 */
function MobilePanelSheet() {
  const [openTab, setOpenTab] = useState<SheetTab | null>(null);
  const pendingPlacementModule = usePendingPlacementModule();

  const toggleTab = useCallback((tab: SheetTab) => {
    setOpenTab((current) => (current === tab ? null : tab));
  }, []);

  // Arming a module from the library is a request to point at the scene, so
  // get the sheet out of the way instead of making the user close it.
  useEffect(() => {
    if (pendingPlacementModule) {
      setOpenTab(null);
    }
  }, [pendingPlacementModule]);

  return (
    <>
      <Drawer
        opened={openTab !== null}
        onClose={() => setOpenTab(null)}
        position="bottom"
        size={openTab ? SHEET_HEIGHTS[openTab] : "70%"}
        // No overlay: the scene stays visible and touchable behind the sheet,
        // which is what lets you check a module against the assembly while
        // the library is open.
        withOverlay={false}
        trapFocus={false}
        lockScroll={false}
        title={
          <SegmentedControl
            size="sm"
            value={openTab ?? "modules"}
            onChange={(value) => setOpenTab(value as SheetTab)}
            data={SHEET_TABS.map(({ value, label }) => ({ value, label }))}
          />
        }
        styles={{
          content: { display: "flex", flexDirection: "column" },
          body: { flex: 1, minHeight: 0, padding: 0 },
          header: { paddingBottom: 8 },
        }}
      >
        {openTab === "modules" && <LeftPanel />}
        {openTab === "inspector" && <RightPanel />}
        {openTab === "joints" && <MobileJointSheet />}
      </Drawer>

      <Box
        style={{
          flexShrink: 0,
          display: "flex",
          borderTop: "1px solid var(--mantine-color-gray-3)",
          backgroundColor: "var(--mantine-color-body)",
          // Clear the home indicator on a viewport-fit=cover phone.
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {SHEET_TABS.map(({ value, label, icon: Icon }) => (
          <UnstyledButton
            key={value}
            onClick={() => toggleTab(value)}
            aria-pressed={openTab === value}
            style={{
              flex: 1,
              height: 56,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2,
              color:
                openTab === value
                  ? "var(--mantine-color-blue-6)"
                  : "var(--mantine-color-dimmed)",
            }}
          >
            <Icon size={20} />
            <Text size="xs">{label}</Text>
          </UnstyledButton>
        ))}
      </Box>
    </>
  );
}

export function App() {
  const {
    sendDeleteInstance,
    sendAddModule,
    sendSaveConfig,
    sendSelectInstance,
    sendUpdateInstancePosition,
    sendTranslationEnd,
    sendConnectModules,
    sendDisconnectInstance,
    sendJointSliderUpdate,
  } = useWebSocket();
  const instances = useInstances();
  const isMobile = useIsMobile();

  // Initialize undo system with all senders
  useEffect(() => {
    initUndoSystem({
      sendAddModule,
      sendDeleteInstance,
      sendUpdateInstancePosition,
      sendTranslationEnd,
      sendConnectModules,
      sendDisconnectInstance,
      sendJointSliderUpdate,
    });
  }, [sendAddModule, sendDeleteInstance, sendUpdateInstancePosition, sendTranslationEnd, sendConnectModules, sendDisconnectInstance, sendJointSliderUpdate]);

  const shortcutHandlers = useMemo(
    () => ({
      onDelete: () => {
        const selected = useViewerStore.getState().selectedModuleInstanceId;
        if (!selected) return;
        const instance = instances.find((i) => i.instance_id === selected);
        if (instance) {
          recordDelete(instance, instances);
          sendDeleteInstance(selected);
        }
      },
      onDuplicate: () => {
        const state = useViewerStore.getState();
        const selected = state.selectedModuleInstanceId;
        if (!selected) return;
        const instance = instances.find((i) => i.instance_id === selected);
        if (!instance) return;
        const pos = instance.position;
        sendAddModule(instance.module_name, [pos[0] + 0.1, pos[1] + 0.1, pos[2]]);
      },
      onSave: () => {
        sendSaveConfig("assembly_config");
      },
      onUndo: () => {
        undo();
      },
      onRedo: () => {
        redo();
      },
      onDeselect: () => {
        useViewerStore.getState().setSelectedMesh(null);
        sendSelectInstance(null);
      },
    }),
    [instances, sendDeleteInstance, sendAddModule, sendSaveConfig, sendSelectInstance]
  );

  useKeyboardShortcuts(shortcutHandlers);

  return (
    <Box
      style={{
        // 100% of #root rather than 100vw/100vh: the root element already
        // tracks the dynamic viewport, and vw would add a horizontal
        // scrollbar's width back on top of it.
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Header />
      <NoticeToaster />

      {isMobile ? (
        <>
          {/* The viewport owns the whole screen; both panels live in the
              bottom sheet below it. */}
          <ViewportDropZone />
          <MobileSelectionBar />
          <MobilePanelSheet />
        </>
      ) : (
        <Box
          style={{
            flex: 1,
            display: "flex",
            overflow: "hidden",
          }}
        >
          {/* Left Panel - Modules */}
          <LeftPanel />

          {/* Center - 3D Viewport with Drop Zone */}
          <ViewportDropZone />

          {/* Right Panel - Inspector */}
          <RightPanel />
        </Box>
      )}
    </Box>
  );
}
